import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanDeactivate, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { BillingComponent } from "../../components/billing/billing.component";
import { PermissionsService } from "../permissions/permissions.service";
import { OrganisationService } from "../organisation/organisation.service";

@Injectable({
  providedIn: 'root'
})
export class BillingGuards implements CanActivate, CanDeactivate<BillingComponent> {
  constructor(
    private permissionsService: PermissionsService,
    private router: Router,
    private organisationService: OrganisationService
  ) {

  }

  /**
   * Guard for accessing routes - controls who can access plan-comparison page and other pages
   */
  canActivate(route: ActivatedRouteSnapshot): boolean | Observable<boolean> {

    if (this.permissionsService.isFeatureActivated('ent-10781-signup-improvements')){
      const redirectToPlanComparison = this.permissionsService.redirectToPlanComparison;
      const path = route.routeConfig?.path;

      if (path === 'plan-comparison') {
        if (redirectToPlanComparison) {
          return true;
        }
        return this.organisationService.getOrganisation().pipe(
          map(org => {
            if (org?.has_one_admin_managed_trial) {
              return true;
            }
            this.router.navigate(['/requests']);
            return false;
          })
        );
      }

      if (path === 'billing') {
        this.router.navigate([redirectToPlanComparison ? '/plan-comparison' : '/requests']);
        return false;
      }

      if (redirectToPlanComparison) {
        this.router.navigate(['/plan-comparison']);
        return false;
      }

      return true;
    } else {
      const isSignUpUser = this.permissionsService.isSignUpUser;

      if (route.routeConfig?.path === 'plan-comparison') {
        this.router.navigate([isSignUpUser ? '/billing' : '/requests']);
        return false;
      }

      // Handle access to the billing page
      if (route.routeConfig?.path === 'billing') {
        // Check if a user is a signup user who needs billing, If the user doesn't need billing, redirect to requests
        if (!isSignUpUser) {
          this.router.navigate(['/requests']);
          return false;
        }

        // Allow access to the billing page for users who need it
        return true;
      }

      // Handle access to other protected pages, If a user needs billing, redirect to the billing page
      if (isSignUpUser) {
        this.router.navigate(['/billing']);
        return false;
      }

      // Allow access to all routes when the user doesn't need billing
      return true;
    }
  }

  /**
   * Guard to prevent leaving the billing page if billing is required
   */
  canDeactivate(component: BillingComponent): boolean {
    if (this.permissionsService.isFeatureActivated('ent-10781-signup-improvements')) {
      const redirectToPlanComparison = this.permissionsService.redirectToPlanComparison;
      return !redirectToPlanComparison;
    } else {
      const isSignUpUser = this.permissionsService.isSignUpUser;
      // Allow navigation away only if the user doesn't need billing
      return !isSignUpUser;
    }
  }
}
